import { Form } from 'react-bootstrap';
import {
  BASIC_FEATURES as BASIC,
  ADMIN_ONLY_FEATURES as ADMIN_ONLY,
  STANDARD_ONLY_FEATURES as STANDARD_ONLY,
} from '@/constants/features';

/**
 * `role` says which browser role the list is for. The standard role also
 * shows the standard-only features, ticked and locked: the stack gives them to
 * the standard role whatever this list says, so a tick here would change
 * nothing. The admin role never shows them, because one more managed policy
 * there fails the deploy.
 *
 * The two lists sit side by side on one page, so every checkbox id carries the
 * role. With a shared id, a click on a label in the admin list ticked the box
 * in the standard list.
 */
export function FeatureChecklist({
  value,
  onChange,
  role,
}: {
  value: string;
  onChange: (v: string) => void;
  role: 'standard' | 'admin';
}) {
  const current = new Set(
    value
      .split(/\r?\n|,/)
      .map((s) => s.trim())
      .filter(Boolean)
  );

  const toggle = (name: string, checked: boolean) => {
    const next = new Set(current);
    if (checked) next.add(name);
    else next.delete(name);
    onChange(Array.from(next).join('\n'));
  };

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-column gap-1">
        {BASIC.map((f) => (
          <Form.Check
            key={f}
            type="checkbox"
            id={`feat-${role}-${f}`}
            label={f}
            checked={current.has(f)}
            onChange={(e) => toggle(f, e.currentTarget.checked)}
          />
        ))}
      </div>
      <div className="text-muted small">Recommended for admins only</div>
      <div className="d-flex flex-column gap-1">
        {ADMIN_ONLY.map((f) => (
          <Form.Check
            key={f}
            type="checkbox"
            id={`feat-${role}-${f}`}
            label={f}
            checked={current.has(f)}
            onChange={(e) => toggle(f, e.currentTarget.checked)}
          />
        ))}
      </div>
      {role === 'standard' && (
        <>
          <div className="text-muted small">
            Always on for the standard role: the Settings pages a custom role gives, at the level it gives.
          </div>
          <div className="d-flex flex-column gap-1">
            {STANDARD_ONLY.map((f) => (
              <Form.Check key={f} type="checkbox" id={`feat-${role}-${f}`} label={f} checked disabled readOnly />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default FeatureChecklist;
